import { Component, TemplateFunction } from '@ribajs/core'
import { EventDispatcher } from '@ribajs/events'
import { scrollTo, waitForProp } from '@ribajs/utils'
import {
  ShopifyProductService,
  AnalyticsService,
  ShopifyCollectionService,
} from '../services/index.js'
import { SbTabsComponent } from './index.js'
import { SbVariantSelectorComponent } from './sb-variant-selector.component.js'
import { SbProductQuantityComponent } from './sb-product-quantity.component.js'
import { DEFAULT_MULTIPACK } from '../constants.js'

import type { LineItemProperties, ScopeSbProduct } from '../types/index.js'

/**
 * Update interval in seconds.
 * Refetches the product data every interval to ensure that the product is up to date
 */
const AUTO_UPDATE_INTERVAL_IN_SECONDS = 600

export class SbProductComponent extends Component {
  public static tagName = 'sb-product'

  protected autoUpdateTimer: number | null = null

  /** The child `sb-variant-selector` to select the variant */
  public childVariantSelector: SbVariantSelectorComponent | null = null

  /** The child `sb-product-quantity` to select the quantity */
  public childProductQuantity: SbProductQuantityComponent | null = null

  /**
   * - handle is the product handle to get the product json object
   * - product is the product object itself if you want to pass it directly
   */
  static get observedAttributes(): string[] {
    return [
      'handle',
      'variant-id',
      'product',
      'track-view',
      'collection-handle',
      'is-recommendation',
      'is-favorite',
      'quantity',
      'allow-trusted-shop-widgets',
      'selling-plan',
    ]
  }

  protected requiredAttributes(): string[] {
    return ['handle']
  }

  public scope: ScopeSbProduct = {
    handle: undefined,
    collectionHandle: undefined,
    isRecommendation: false,
    isFavorite: false,
    isFreePresent: false,
    product: undefined,
    variant: undefined,
    featured_image: {},
    options: [],
    quantity: 1,
    sellingPlan: undefined,
    availableSellingPlans: [],
    metafields: null,
    colliLabel: '',
    multipacks: [DEFAULT_MULTIPACK],
    selectedMultipack: undefined,
    goToRating: this.goToRating,

    badges: {
      new: false,
      sale: 0,
      saleSum: 0,
      soldOut: false,
      outOfStockText: '',
      discountInfo: '',
      additionalInfo: '',
    },

    trackView: true,

    ratingTemplate: '',
    ratingSmallTemplate: '',

    prices: null,
  }

  protected events = EventDispatcher.getInstance()

  constructor() {
    super()
    this.onVariantChanged = this.onVariantChanged.bind(this)
    this.onMultipackSelected = this.onMultipackSelected.bind(this)
  }

  /**
   * Prepare product, remove protocol from featured_image, lowercase the option names
   * @param product product object
   */
  protected async onProductChanged() {
    this.scope.handle = this.scope.handle || this.scope.product?.handle

    if (!this.scope.product?.sbIsPrepared) {
      console.error(
        `[SbProductComponent.onProductChanged] Product is not prepared: `,
        this.scope.product,
      )
      // return await this.setProductData(false)
      return
    }
  }

  public async setProductData(enableCache: boolean) {
    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    const { product, metafields, bgColor, variant } =
      await ShopifyProductService.getPreparedProductData(
        this.scope.handle,
        this.scope.variantId,
        enableCache,
      )
    if (this.scope.variantId && !variant) {
      console.warn(
        `[SbProductComponent.setProductData] Invalid variant id ${this.scope.variantId}`,
      )
      this.scope.variantId = undefined
    }
    this.scope.product = product
    this.scope.metafields = metafields
    this.scope.variant = variant

    if (this.scope.variant) {
      const { badges, featuredImage, multipacks, colliLabel } =
        await ShopifyProductService.getPreparedProductVariantData(
          this.scope.product,
          this.scope.variant,
          this.scope.metafields,
        )
      this.scope.badges = badges
      this.scope.featured_image = featuredImage
      this.scope.multipacks = multipacks
      this.scope.colliLabel = colliLabel
    }

    this.onProductChanged()

    ShopifyProductService.setImageBgColor(this, bgColor)
  }

  protected updateRatingTemplates() {
    const oldBarcode = this.scope.currentBarcode
    const newBarcode = this.scope.variant?.barcode
    const barcodeChanged = oldBarcode !== newBarcode

    if (!barcodeChanged) {
      return
    }

    this.scope.currentBarcode = this.scope.variant?.barcode

    // Only reload the rating template if a barcode is set
    if (this.scope.variant?.barcode) {
      // This is a WORKAROUND for the `etrusted-widget` to force template reloading
      const ratingTemplates = ShopifyProductService.getRatingTemplates(
        'text-base font-body',
        this.scope.currentBarcode,
      )

      // Set to empty string to force an template update
      if (this.scope.ratingTemplate.length > 0) this.scope.ratingTemplate = ''
      this.scope.ratingTemplate = ratingTemplates.normal

      // Set to empty string to force an template update
      if (this.scope.ratingSmallTemplate.length > 0)
        this.scope.ratingSmallTemplate = ''
      this.scope.ratingSmallTemplate = ratingTemplates.small
    } else {
      if (this.scope.ratingTemplate.length > 0) this.scope.ratingTemplate = ''
      if (this.scope.ratingSmallTemplate.length > 0)
        this.scope.ratingSmallTemplate = ''
    }
  }

  protected async connectedCallback() {
    super.connectedCallback()
    this.init(SbProductComponent.observedAttributes)

    this.initScopeWatcher()
  }

  // deconstruction
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
    this.stopAutoUpdater()
  }

  /**
   * Watches for scope changes and executes specific actions
   */
  protected initScopeWatcher() {
    this.scope = new Proxy(this.scope, {
      set: (target, prop: keyof ScopeSbProduct, newValue, receiver) => {
        const currentValue = this.scope[prop]
        const result = Reflect.set(target, prop, newValue, receiver)

        if (currentValue === newValue) {
          return result
        }

        if (
          prop === 'quantity' ||
          prop === 'variant' ||
          prop === 'sellingPlan'
        ) {
          // Update pricing info when related props change
          this.refreshPrices()
        }

        return result
      },
    })
  }

  /**
   * Additional line item cart properties, used for tracking
   * @returns
   */
  protected getProperties() {
    const cartProperties: LineItemProperties = {}
    cartProperties._isRecommendation = this.scope.isRecommendation || false
    cartProperties._isFavorite = this.scope.isFavorite || false
    cartProperties._isFreePresent = this.scope.isFreePresent || false

    return cartProperties
  }

  public goToRating() {
    const tabsEl = this.querySelector<SbTabsComponent>(SbTabsComponent.tagName)

    if (!tabsEl) {
      console.error(`Element with tag ${SbTabsComponent.tagName} not found!`)
      return
    }

    tabsEl?.showByName('reviews')
    scrollTo(tabsEl, 100)
  }

  /**
   * Updates the product data without cache to ensure that the product is up to date
   * Called in regular to keep the product up-to-date
   */
  protected async updateData() {
    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    // Make sure to restore the previously selected variant
    if (this.scope.variant?.id) {
      this.scope.variantId = this.scope.variant?.id
    }

    await this.setProductData(false)
  }

  /** Init the product data auto updater */
  protected initAutoUpdater() {
    this.autoUpdateTimer = setInterval(() => {
      this.updateData()
    }, AUTO_UPDATE_INTERVAL_IN_SECONDS * 1000)
  }

  protected stopAutoUpdater() {
    if (this.autoUpdateTimer !== null) {
      clearInterval(this.autoUpdateTimer)
    }
  }

  protected async beforeBind() {
    await super.beforeBind()

    if (!this.scope.handle && !this.scope.product) {
      throw new Error('Product handle or product not set!')
    }

    // Use the cache the first time
    await this.setProductData(true)

    this.setElements()
    await this.addEventListeners()
  }

  protected async afterBind() {
    await super.afterBind()

    if (!this.scope.handle) {
      throw new Error('Product handle not set!')
    }

    if (this.scope.collectionHandle) {
      this.scope.parentCollectionHandle = ShopifyCollectionService.getParent(
        this.scope.collectionHandle,
      )
    }

    this.initAutoUpdater()

    this.classList.remove('opacity-0')
    this.classList.add(`${SbProductComponent.tagName}-ready`, 'opacity-100')

    await this.trackPdpView()
  }

  protected async trackPdpView() {
    await waitForProp('currency', window.Shopify, 10000)
    if (!window.Shopify?.currency) {
      console.warn('window.Shopify.currency is not defined!')
      return
    }

    if (
      this.scope.trackView &&
      this.scope.product &&
      this.scope.variant &&
      window.Shopify.currency
    )
      AnalyticsService.getInstance()?.trackPdpView(
        this.scope.product,
        this.scope.variant,
        window.Shopify?.currency.active,
        this.scope.quantity || 0,
        this.getProperties(),
      )
  }

  protected setElements() {
    this.setChildVariantSelector()
    this.setChildProductQuantity()
  }

  protected setChildVariantSelector() {
    if (!this.childVariantSelector) {
      this.childVariantSelector = this.querySelector(
        SbVariantSelectorComponent.tagName,
      )
    }
  }

  protected setChildProductQuantity() {
    if (!this.childProductQuantity) {
      this.childProductQuantity = this.querySelector(
        SbProductQuantityComponent.tagName,
      )
    }
  }

  protected async addEventListeners() {
    this.childVariantSelector?.addEventListener(
      'variant-change',
      this.onVariantChanged as EventListener,
    )
    this.childProductQuantity?.addEventListener(
      'multipack-selected',
      this.onMultipackSelected as EventListener,
    )
  }

  protected removeEventListeners() {
    this.childVariantSelector?.removeEventListener(
      'variant-change',
      this.onVariantChanged as EventListener,
    )
    this.childProductQuantity?.removeEventListener(
      'multipack-selected',
      this.onMultipackSelected as EventListener,
    )
  }

  protected async onMultipackSelected() {
    if (!this.scope.product || !this.scope.variant) {
      console.error(
        `[SbProductComponent.onMultipackSelected] Product or variant is not set!`,
      )
      return
    }

    this.scope.selectedMultipack = this.childProductQuantity!.activeMultipack
    this.refreshPrices()

    const { colliLabel, badges } =
      await ShopifyProductService.getPreparedProductVariantData(
        this.scope.product,
        this.scope.variant,
        this.scope.metafields,
        this.scope.selectedMultipack,
      )
    this.scope.colliLabel = colliLabel
    this.scope.badges = badges
  }

  /** Called if the variant was changed using SbVariantSelectorComponent */
  protected async onVariantChanged() {
    this.updateRatingTemplates()

    const { product, variant } = this.scope

    if (!product || !variant || !product.sbIsPrepared) {
      console.error(
        `[SbProductComponent.onVariantChanged] Product is not prepared: `,
        product,
      )
      return
    }

    if (ShopifyProductService.hiddenOnMarket(variant.metafields)) {
      console.warn(
        `[SbProductComponent.onVariantChanged] Variant is hidden on current market!`,
        variant,
      )
      this.scope.variant = undefined
      this.scope.variantId = undefined
      return
    }

    // Deselect multipack on variant change
    this.scope.selectedMultipack = undefined

    const { badges, featuredImage, multipacks, colliLabel } =
      await ShopifyProductService.getPreparedProductVariantData(
        product,
        variant,
        this.scope.metafields,
        this.scope.selectedMultipack,
      )
    this.scope.badges = badges
    this.scope.featured_image = featuredImage
    if (multipacks !== this.scope.multipacks) {
      this.scope.multipacks = multipacks
    }
    this.scope.colliLabel = colliLabel

    this.scope.availableSellingPlans = variant.available
      ? ShopifyProductService.getSellingPlans(product, variant)
      : []
  }

  private refreshPrices() {
    if (!this.scope.variant) {
      this.scope.prices = null
      return
    }

    let subscriptionFactor = 1
    let multipackFactor = 1
    const variantPrice = this.scope.variant.price

    const originalPricePerItem =
      this.scope.variant.compare_at_price ?? variantPrice

    // We always want to show prices therefore we enforce a minimum quantity of 1
    const quantity = Math.max(1, this.scope.quantity)

    const selectedMultipack = this.childProductQuantity?.activeMultipack
    if (selectedMultipack) {
      // scope.quantity already has multipack multiplicator applied

      multipackFactor = 1 - selectedMultipack.percentage / 100
    }

    if (this.scope.sellingPlan) {
      const [firstAdjustment, ...moreAdjustments] =
        this.scope.sellingPlan.price_adjustments

      if (!firstAdjustment) {
        console.error(
          'No price adjustment found for selling plan. Ignoring it.',
          this.scope.sellingPlan,
        )
      } else if (moreAdjustments.length > 0) {
        console.error(
          `More than one price adjustment found for selling plan. Ignoring it.`,
          this.scope.sellingPlan,
        )
      } else if (firstAdjustment.value_type !== 'percentage') {
        console.error(
          `Only percentage adjustments are supported! Ignoring selling plan.`,
          this.scope.sellingPlan,
        )
      } else {
        subscriptionFactor = 1 - firstAdjustment.value / 100
      }
    }

    // Prices are calculated as full cents

    // Calculation has been reverseengineered in #65179
    const newPrices: ScopeSbProduct['prices'] = {
      final: Number.POSITIVE_INFINITY, // Placeholder will be replaced in the following code
      original: originalPricePerItem * quantity,
      perUnit: null,
      discountPercentage: null,
      discountLabel: null,
    }

    if (
      this.scope.variant.unit_price_measurement?.measured_type === 'weight' &&
      this.scope.variant.unit_price_measurement.quantity_unit === 'g'
    ) {
      const weight = parseFloat(
        this.scope.variant.unit_price_measurement.quantity_value,
      )

      if (weight <= 0) {
        console.error(
          `[SbProductComponent.refreshPrices] Unsupported unit price measurement: ${JSON.stringify(this.scope.variant.unit_price_measurement)}`,
        )
      } else {
        // Formula. Copy from seeberger-shop-utilities. Look for [MARKER-PRICE-FORMULA]
        const unitPricePrecise =
          (Math.ceil(
            Math.round(variantPrice * subscriptionFactor) * multipackFactor,
          ) *
            1000) /
          weight // Price in full cents but not necessarily as integer
        const unitPrice = Math.round(unitPricePrecise) // Price in full cents as integer
        const finalPrice = Math.round(
          (unitPricePrecise * quantity * weight) / 1000,
        ) // Price in full cents as integer

        newPrices.perUnit = unitPrice
        newPrices.final = finalPrice
      }
    }

    if (newPrices.final === newPrices.original) {
      newPrices.original = null
    } else if (newPrices.original) {
      newPrices.discountPercentage = Math.round(
        100 * (1 - newPrices.final / newPrices.original),
      )
      newPrices.discountLabel = ShopifyProductService.getDiscountLabel(
        this.scope.variant,
      )
    }

    this.scope.prices = newPrices
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
